import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { useUser } from '../UserContext';
import { BASE_URL } from '../conf/config';
import { openSuperset } from '../components/openSuperset';
import './Dashboard.css';

const SDK = `${process.env.PUBLIC_URL}/vendor/superset-embedded-sdk-0.4.0.js`;
let sdkLoading = null;

// Superset's embedding SDK (public/vendor), loaded once, on the first visit of this page
function loadSdk() {
  if (!sdkLoading) {
    sdkLoading = new Promise((resolve, reject) => {
      if (window.supersetEmbeddedSdk) { resolve(window.supersetEmbeddedSdk); return; }
      const script = document.createElement('script');
      script.src = SDK;
      script.onload = () => resolve(window.supersetEmbeddedSdk);
      script.onerror = () => { sdkLoading = null; reject(new Error('The dashboard viewer could not be loaded.')); };
      document.head.appendChild(script);
    });
  }
  return sdkLoading;
}

/**
 * The person's home dashboard from Superset, shown here read-only (Superset's embedding: the backend hands out a
 * guest token per viewer, renewed every few minutes). Building, changing and the AI are in Superset itself:
 * "Open in Superset". Which dashboard is home is picked in Superset ("Make this my home dashboard").
 */
const DashboardPage = () => {
  const { fetchWithAuth } = useUser();
  const fetchRef = useRef(fetchWithAuth); // fetchWithAuth is new on every render; the effects must not follow it
  fetchRef.current = fetchWithAuth;
  const mount = useRef(null);
  const [home, setHome] = useState(undefined); // undefined: loading, null: none yet
  const [supersetUrl, setSupersetUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchRef.current(`${BASE_URL}/api/reporting/home/?embed=1`)
      .then((r) => r.json().catch(() => ({})).then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (!ok) { setError(data.errors?.[0] || 'Superset cannot be reached right now.'); return; }
        if (!data.configured) { setError('Superset is not set up on this server.'); return; }
        setSupersetUrl(data.superset_url);
        setHome(data.dashboard);
      })
      .catch(() => !cancelled && setError('The server could not be reached.'));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!home || !home.embed || !mount.current) return undefined;
    let cancelled = false;
    let shown = null;
    loadSdk()
      .then((sdk) => sdk.embedDashboard({
        id: home.embed,
        supersetDomain: supersetUrl,
        mountPoint: mount.current,
        fetchGuestToken: async () => {
          const r = await fetchRef.current(`${BASE_URL}/api/reporting/home/token/`, { method: 'POST' });
          const data = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(data.errors?.[0] || 'No access to this dashboard.');
          return data.token;
        },
        dashboardUiConfig: { hideTitle: true, filters: { expanded: false } },
        // Superset checks that the page showing it is the ERP: the origin must reach it as the referrer
        referrerPolicy: 'strict-origin-when-cross-origin',
      }))
      .then((dashboard) => { shown = dashboard; if (cancelled) dashboard.unmount(); })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; if (shown) shown.unmount(); };
  }, [home, supersetUrl]);

  const open = (next) => openSuperset(fetchRef.current, next);

  if (error) {
    return <Alert type="warning" showIcon message="Dashboard" description={error} />;
  }
  if (home === undefined) {
    return <div className="db-loading"><Spin /></div>;
  }
  if (home === null) {
    return (
      <div className="db-empty">
        <h3>No home dashboard yet</h3>
        <div className="db-text">
          Open Superset, go to a dashboard and click <b>Make this my home dashboard</b>. It then shows here. In
          Superset you can also build dashboards yourself, or have the AI build them (<b>New dashboard with AI</b>).
        </div>
        <Button type="primary" onClick={() => open('/dashboard/list/')}>Open Superset</Button>
      </div>
    );
  }
  return (
    <div className="db-page">
      <div className="db-head">
        <span className="db-title">{home.title}</span>
        <span className="db-hint">
          {home.own ? 'Your home dashboard' : 'Your group\'s dashboard'}. To change it: open another dashboard in
          Superset and click "Make this my home dashboard".
        </span>
        <Button onClick={() => open('/dashboard/list/')}>All dashboards</Button>
        <Button type="primary" onClick={() => open(`/superset/dashboard/${home.id}/`)}>Open in Superset</Button>
      </div>
      <div className="db-frame" ref={mount} />
    </div>
  );
};

export default DashboardPage;
